import { Box, Text } from "@mantine/core";
import type { VideoRef } from "@/content/types";
import { resolvePublic } from "@/lib/media";

interface Props {
  video: VideoRef;
}

/**
 * Plays both kinds of VideoRef:
 *  - kind="stream"  → embeds the Cloudflare Stream iframe.
 *  - kind="file"    → plain HTML5 <video> pointing at an R2-hosted MP4.
 *
 * Both render inside a position:relative 16:9 wrapper with the player
 * absolutely positioned inside, so the player CAN'T affect the page layout
 * when it loads metadata, starts playback, or hits the browser's intrinsic
 * sizing fallback. (Without this, Chrome in particular tries to use the
 * video's native pixel dimensions on play and overflows surrounding
 * content; that's what was causing the "expand → collapse" flicker.)
 */
export function StreamPlayer({ video }: Props) {
  if (video.kind === "file") {
    return <FileVideo video={video} />;
  }
  return <StreamEmbed video={video} />;
}

const FRAME_STYLE: React.CSSProperties = {
  position: "relative",
  width: "100%",
  aspectRatio: "16 / 9",
  background: "var(--color-deeper)",
  borderRadius: 8,
  overflow: "hidden",
};

const ABSOLUTE_FILL_STYLE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  border: 0,
  display: "block",
};

function FileVideo({ video }: { video: Extract<VideoRef, { kind: "file" }> }) {
  const src =
    video.ref.kind === "external"
      ? video.ref.url
      : resolvePublic(video.ref.key);
  const poster = video.posterKey ? resolvePublic(video.posterKey) : undefined;

  return (
    <Box style={FRAME_STYLE}>
      <video
        src={src}
        poster={poster}
        controls
        playsInline
        preload="metadata"
        // object-fit: contain so the full frame is visible (letterboxes
        // non-16:9 sources instead of cropping). Switched from "cover"
        // because cropping music-video content is rarely what you want.
        style={{ ...ABSOLUTE_FILL_STYLE, objectFit: "contain" }}
      />
    </Box>
  );
}

function StreamEmbed({ video }: { video: Extract<VideoRef, { kind: "stream" }> }) {
  const STREAM_CUSTOMER = import.meta.env.VITE_STREAM_CUSTOMER ?? "";

  if (!STREAM_CUSTOMER) {
    return (
      <Box
        style={{
          ...FRAME_STYLE,
          border: "1px dashed var(--color-ocean)",
          background: "var(--color-deep)",
        }}
      >
        <Box
          style={{
            ...ABSOLUTE_FILL_STYLE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text size="sm" c="dimmed">
            Video placeholder ({video.uid || "no uid"})
          </Text>
        </Box>
      </Box>
    );
  }

  const src = `https://${STREAM_CUSTOMER}.cloudflarestream.com/${video.uid}/iframe`;

  return (
    <Box style={FRAME_STYLE}>
      <iframe
        src={src}
        title={`stream ${video.uid}`}
        loading="lazy"
        style={ABSOLUTE_FILL_STYLE}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
      />
    </Box>
  );
}
